import { type StoreObject, useReactiveVar } from "@apollo/client";
import { Link } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";

import SignupModal from "@/components/signup-modal";
import { Button } from "@/components/ui/button";
import { cache } from "@/helpers/apollo";
import useAnalytics from "@/hooks/use-analytics";
import useAuth from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import { user as userStore } from "@/store";

import useFollowUser from "../hooks/use-follow-user";
import useUnfollowUser from "../hooks/use-unfollow-user";
import type { UserType } from "../types";
import BlockedButton from "./blocked-button";
import ModalButton from "./modal-button";
import UnfollowModal from "./unfollow-modal";

const Labels = {
  BLOCKED: "Blocked",
  EDIT: "Edit profile",
  FOLLOW: "Follow",
  FOLLOW_BACK: "Follow back",
  UNFOLLOW: "Un-follow",
} as const;

interface ActionsProps {
  user: UserType;
}

export default function Actions({ user }: ActionsProps) {
  const [hoverDisabled, setHoverDisabled] = useState(false);

  const currentUser = useReactiveVar(userStore);

  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [isUnfollowModalOpen, setIsUnfollowModalOpen] = useState(false);

  const { isUser, mutationContext } = useAuth();
  const { track } = useAnalytics();
  const [followUser] = useFollowUser();
  const [unfollowUser] = useUnfollowUser();

  const toggleFollowing = useCallback(() => {
    if (!isUser) {
      setIsSignupModalOpen(true);
      return;
    }

    cache.modify({
      fields: {
        isFollowing: (value) => {
          const newValue = !value;
          (newValue ? followUser : unfollowUser)(
            mutationContext({ userId: user.id })
          );

          if (newValue) {
            setHoverDisabled(true);
          }

          return newValue;
        },
      },
      id: cache.identify(user as unknown as StoreObject),
    });

    track(user.isFollowing ? "User Unfollowed" : "User Followed", {
      user: user.id,
    });
  }, [isUser, user, followUser, unfollowUser, mutationContext, track]);

  const handleMouseLeave = () => setHoverDisabled(false);

  const label = useMemo(() => {
    if (currentUser && currentUser.id === user.id) {
      return Labels.EDIT;
    }
    if (user.isBlocked) {
      return Labels.BLOCKED;
    }
    if (user.isFollowing) {
      return Labels.UNFOLLOW;
    }
    if (user.isFollower) {
      return Labels.FOLLOW_BACK;
    }
    return Labels.FOLLOW;
  }, [currentUser, user]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: no need
  const button = useMemo(() => {
    if (label === Labels.BLOCKED) {
      return <BlockedButton label={Labels.BLOCKED} user={user} />;
    }

    if (label === Labels.UNFOLLOW) {
      return (
        <Button
          className={cn(
            'h-[45px] w-full shrink rounded-[22px] border border-white/50 font-medium text-14 shadow-bright-red/40 transition-[colors,shadow] before:content-["Following"]',
            !hoverDisabled &&
              'hover:border-bright-red/50 hover:bg-bright-red/10 hover:text-bright-red hover:shadow-0-0-10 hover:before:content-["Unfollow"]'
          )}
          key="action"
          onClick={() => setIsUnfollowModalOpen(true)}
          onMouseLeave={handleMouseLeave}
        />
      );
    }
    const buttonProps: React.ComponentProps<typeof Button> = {
      className: cn("h-[45px] w-full shrink-1 rounded-[22px]"),
      variant: "cta",
    };

    if (label === Labels.EDIT) {
      return (
        <Button asChild key="action" {...buttonProps}>
          <Link to={`/@${user.username}/edit-profile`}>{label}</Link>
        </Button>
      );
    }

    return (
      <Button key="action" onClick={toggleFollowing} {...buttonProps}>
        {label}
      </Button>
    );
  }, [label, toggleFollowing, user, hoverDisabled]);

  return (
    <>
      <div className="mt-[15px] flex items-center gap-2.5 md:mt-0">
        {button}
        {label !== Labels.BLOCKED && (
          <ModalButton currentUser={currentUser} key="more" user={user} />
        )}
      </div>
      <SignupModal
        onOpenChange={setIsSignupModalOpen}
        open={isSignupModalOpen}
      />
      <UnfollowModal
        onOpenChange={setIsUnfollowModalOpen}
        onUnfollow={toggleFollowing}
        open={isUnfollowModalOpen}
        username={user.username}
      />
    </>
  );
}
